import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { createLocation, updateLocation, getLocation } from '../../../shared/api/locations.js'
import { getCities, type CityRef } from '../../../shared/api/cities.js'
import { validateNameField } from '../../../shared/utils/catalog.js'
import { toFieldErrors } from '../../../shared/utils/formErrors.js'
import { ROUTES } from '../../../app/routes.js'

const TIMEZONES = [
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Europe/Samara',
  'Europe/Volgograd',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Magadan',
  'Asia/Kamchatka',
]

interface Props {
  organizationId: string
  locationId: string | null // null = create mode
}

export default function LocationForm({ organizationId, locationId }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const isEdit = locationId !== null

  useEffect(() => {
    getCities()
      .then(list => setCities(list.filter(c => c.status === 'ACTIVE')))
      .catch(() => setCities([]))
  }, [])

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [address, setAddress] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [timezone, setTimezone] = useState('Europe/Moscow')
  const [cityId, setCityId] = useState('')
  const [cities, setCities] = useState<CityRef[]>([])
  const [latStr, setLatStr] = useState('')
  const [lngStr, setLngStr] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    if (!isEdit) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setLoadError('')
      try {
        const loc = await getLocation(organizationId, locationId)
        if (cancelled) return
        setName(loc.name)
        setSlug(loc.slug ?? '')
        setDescription(loc.description ?? '')
        setAddress(loc.address ?? '')
        setPhone(loc.phone ?? '')
        setEmail(loc.email ?? '')
        setTimezone(loc.timezone)
        setCityId(loc.cityId ?? '')
        setLatStr(loc.latitude !== null ? String(loc.latitude) : '')
        setLngStr(loc.longitude !== null ? String(loc.longitude) : '')
        setErrors({})
        setLoading(false)
      } catch {
        if (!cancelled) setLoadError(t('locations.loadError'))
        setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [organizationId, locationId])

  function handleChange() {
    if (errors.general) setErrors({})
  }

  function validate(): Record<string, string> {
    const fieldErrors: Record<string, string> = {}
    const nameErr = validateNameField(name)
    if (nameErr === 'required') fieldErrors.name = t('error.fieldRequired')
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      fieldErrors.email = t('error.emailInvalid')
    }
    return fieldErrors
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()

    const fieldErrors = validate()
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    setSaving(true)
    setErrors({})

    const payload: Record<string, unknown> = {
      name: name.trim(),
      description: description.trim() || undefined,
      address: address.trim() || undefined,
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
      timezone,
    }

    if (!isEdit) {
      payload.slug = slug.trim() || undefined
    }

    if (isEdit) {
      payload.cityId = cityId || null
    } else if (cityId) {
      payload.cityId = cityId
    }

    const lat = latStr.trim()
    const lng = lngStr.trim()
    if (lat) payload.latitude = Number(lat)
    if (lng) payload.longitude = Number(lng)

    try {
      if (isEdit) {
        await updateLocation(organizationId, locationId, payload as any)
      } else {
        await createLocation(organizationId, payload as any)
      }
      navigate(ROUTES.LOCATIONS)
    } catch (err: unknown) {
      const fields = isEdit
        ? ['name', 'description', 'address', 'phone', 'email', 'cityId']
        : ['name', 'slug', 'description', 'address', 'phone', 'email', 'cityId']
      setErrors(toFieldErrors(err, fields as any, t('error.serverError')) as any)
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <section className="page-section">
        <h2>{t('app.locations')}</h2>
        <div className="page-loading">
          <div className="spinner" />
        </div>
      </section>
    )
  }

  if (loadError) {
    return (
      <section className="page-section">
        <h2>{t('app.locations')}</h2>
        <div className="settings-card">
          <div className="form-error-banner">{loadError}</div>
          <button type="button" className="btn btn-primary" onClick={() => navigate(ROUTES.LOCATIONS)}>
            {t('menu.back')}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="page-section">
      <div className="page-header">
        <div>
          <h2>{isEdit ? t('locations.editTitle') : t('locations.createTitle')}</h2>
          <p className="page-section-description">
            {isEdit ? t('locations.editSubtitle') : t('locations.createSubtitle')}
          </p>
        </div>
      </div>

      <div className="settings-card">
        {errors.general && <div className="form-error-banner">{errors.general}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="locName">{t('locations.name')} *</label>
              <input
                id="locName"
                type="text"
                value={name}
                onChange={e => { setName(e.target.value); handleChange() }}
                maxLength={200}
                required
                disabled={saving}
                autoComplete="off"
              />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>

            {!isEdit && (
              <div className="form-field">
                <label htmlFor="locSlug">{t('menu.slug')}</label>
                <input
                  id="locSlug"
                  type="text"
                  value={slug}
                  onChange={e => { setSlug(e.target.value); handleChange() }}
                  maxLength={100}
                  disabled={saving}
                  autoComplete="off"
                />
                <span className="field-hint">{t('menu.slugHint')}</span>
                {errors.slug && <span className="field-error">{errors.slug}</span>}
              </div>
            )}
          </div>

          {isEdit && (
            <div className="form-field">
              <label htmlFor="locSlugReadonly">{t('menu.slug')}</label>
              <input id="locSlugReadonly" type="text" value={slug} readOnly disabled />
              <span className="field-hint">{t('menu.slugReadonlyHint')}</span>
            </div>
          )}

          <div className="form-field">
            <label htmlFor="locDescription">{t('locations.description')}</label>
            <textarea
              id="locDescription"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              maxLength={2000}
              disabled={saving}
            />
          </div>

          <div className="form-field">
            <label htmlFor="locAddress">{t('locations.address')}</label>
            <input
              id="locAddress"
              type="text"
              value={address}
              onChange={e => setAddress(e.target.value)}
              maxLength={500}
              disabled={saving}
              autoComplete="off"
            />
            {errors.address && <span className="field-error">{errors.address}</span>}
          </div>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="locPhone">{t('restaurant.phone')}</label>
              <input
                id="locPhone"
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                maxLength={50}
                disabled={saving}
                autoComplete="off"
              />
              {errors.phone && <span className="field-error">{errors.phone}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="locEmail">Email</label>
              <input
                id="locEmail"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                maxLength={255}
                disabled={saving}
                autoComplete="off"
              />
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="locLatitude">{t('locations.latitude')}</label>
              <input
                id="locLatitude"
                type="number"
                value={latStr}
                onChange={e => setLatStr(e.target.value)}
                step="any"
                min="-90"
                max="90"
                disabled={saving}
              />
            </div>

            <div className="form-field">
              <label htmlFor="locLongitude">{t('locations.longitude')}</label>
              <input
                id="locLongitude"
                type="number"
                value={lngStr}
                onChange={e => setLngStr(e.target.value)}
                step="any"
                min="-180"
                max="180"
                disabled={saving}
              />
            </div>

            <div className="form-field">
              <label htmlFor="locCity">{t('locations.city')}</label>
              <select
                id="locCity"
                value={cityId}
                onChange={e => setCityId(e.target.value)}
                disabled={saving}
              >
                <option value="">{t('locations.cityNone')}</option>
                {cities.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {errors.cityId && <span className="field-error">{errors.cityId}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="locTimezone">{t('restaurant.timezone')}</label>
              <select
                id="locTimezone"
                value={timezone}
                onChange={e => setTimezone(e.target.value)}
                disabled={saving}
              >
                {TIMEZONES.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
              {errors.timezone && <span className="field-error">{errors.timezone}</span>}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? (isEdit ? t('restaurant.saving') : t('locations.creating')) : (isEdit ? t('restaurant.save') : t('locations.create'))}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => navigate(ROUTES.LOCATIONS)} disabled={saving}>
              {t('menu.cancel')}
            </button>
          </div>
        </form>
      </div>
    </section>
  )
}